
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { ClientValidation } from '@/modules/GatewayModule/validations/Client.validation'

// classes
import { DataState } from '@/core/classes/DataState.class'
import { gateway } from '@/core/realisations/gateway.realisation'
import { users } from '@/core/realisations/users.realisation'

// types
import { GatewayClientT, GatewayScopeT } from '@/core/types/gateway.types'
import { UserT } from '@/core/types/users.types'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-select': TheSelect,
        checkbox: Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<GatewayClientT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel', 'delete'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            name: null as string | null,
            redirect: null as string | null,
            scopes: [] as number[],
            password: false,
            personalToken: false,
            serviceClient: false,
            user_id: null as number | null,
        })

        const state_form$ = useVuelidate(ClientValidation, state)

        const data_state = reactive({
            scopes: new DataState<GatewayScopeT>({}),
            users: new DataState<UserT>({}),
        })

        const loadScopes = async (page: number) => {
            data_state.scopes.pagination.current_page = page

            try {
                data_state.scopes.is_loading = true

                const response = await gateway.getScopes(page)
                data_state.scopes.list = response.data
                data_state.scopes.pagination.total_pages = response.meta.last_page

                data_state.scopes.is_failed = false
                data_state.scopes.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.scopes.failure.catchError(error)

                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_failed = true
                }
            } finally {
                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_loading = false
                }
            }
        }

        const loadMoreScopes = async (page: number) => {
            if (page > data_state.scopes.pagination.total_pages) return
            data_state.scopes.pagination.current_page = page

            try {
                data_state.scopes.is_loading = true

                const response = await gateway.getScopes(page)
                data_state.scopes.list = data_state.scopes.list.concat(response.data)
                data_state.scopes.pagination.total_pages = response.meta.last_page

                data_state.scopes.is_failed = false
                data_state.scopes.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.scopes.failure.catchError(error)

                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_failed = true
                }
            } finally {
                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_loading = false
                }
            }
        }

        const loadUsers = async (page: number, search?: string) => {
            data_state.users.pagination.current_page = page

            try {
                data_state.users.is_loading = true

                const data = await users.getList({
                    page,
                    email: search,
                })

                data_state.users.list = data.data

                data_state.users.pagination.total_pages = data.meta.last_page

                data_state.users.is_failed = false
                data_state.users.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.users.failure.catchError(error)

                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_failed = true
                }
            } finally {
                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_loading = false
                }
            }
        }

        const refreshUsers = () => {
            loadUsers(data_state.users.pagination.current_page)
        }

        const loadMoreUsers = async (page: number) => {
            if (page > data_state.users.pagination.total_pages) return
            data_state.users.pagination.current_page = page

            try {
                data_state.users.is_loading = true

                const response = await users.getList({ page })
                data_state.users.list = data_state.users.list.concat(response.data)

                data_state.users.pagination.total_pages = response.meta.last_page

                data_state.users.is_failed = false
                data_state.users.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.users.failure.catchError(error)

                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_failed = true
                }
            } finally {
                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_loading = false
                }
            }
        }

        const searchUser = (search_query: string) => {
            loadUsers(1, search_query)
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление клиента'
            if (props.isUpdate) return 'Редактирование клиента'
            if (props.isDelete) return 'Удаление клиента'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const payload = {
                name: state.name,
                redirect: state.redirect,
                scopes: state.scopes,
                password: state.password,
                personalToken: state.personalToken,
                serviceClient: state.serviceClient,
                user_id: state.user_id || undefined,
            }

            emit('create', payload)
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', toRaw(state))
        }

        const emitDelete = () => {
            emit('delete', state.id)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        onBeforeMount(() => {
            loadScopes(1)
            loadUsers(1)
            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.name = props.initialValue.name
            state.redirect = props.initialValue.redirect
            state.password = props.initialValue.password_client
            state.personalToken = props.initialValue.personal_access_client
            state.serviceClient = props.initialValue.service_client
            state.scopes = props.initialValue.scopes.map((scope) => scope.id)
            state.user_id = props.initialValue.user_id
        })

        return {
            state,
            state_form$,
            data_state,
            loadScopes,
            loadMoreScopes,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            loadUsers,
            refreshUsers,
            loadMoreUsers,
            searchUser,
        }
    },
})
